import { select } from 'd3-selection'
import { scalePoint, scaleLinear, scaleBand } from 'd3-scale'
import { axisBottom } from 'd3-axis'
import { max } from 'd3-array'
import { line, area } from 'd3-shape'
import debounce from 'lodash/debounce'

export default {
  name: 'my-chart',
  template: `
    <div class="o-chart__svg">
      <svg preserveAspectRatio="none" ref="svg"></svg>
    </div>
  `,
  props: {
    file: {
      type: String,
      required: true
    },
    color: {
      type: [String, Array],
      required: true
    },
    startWidth: {
      type: Number,
      default: 300
    },
    startHeight: {
      type: Number,
      default: 168
    },
    margin: {
      type: Object,
      default () {
        return {
          top: 20,
          right: 18,
          bottom: 20,
          left: 15
        }
      }
    },
    replaceNa: {
      type: Boolean,
      default: false
    },
    chartType: {
      type: String,
      default: 'line'
    },
    baseStart: {
      type: Number,
      default: 0
    },
    back: {
      type: [Boolean, Number],
      default: false
    },
    offset: {
      type: [Boolean, Number],
      default: false
    },
    skew: {
      type: Number,
      default: 0
    },
    padding: {
      type: Number,
      default: 0.05
    }
  },
  data () {
    return {
      svg: null,
      canvas: null,
      chartData: [],
      clientWidth: null
    }
  },
  computed: {
    width () {
      return this.clientWidth - this.margin.left - this.margin.right
    },
    height () {
      return this.startHeight - this.margin.top - this.margin.bottom
    },
    origData () {
      return require('@/assets/data/' + this.file + '.csv')
    }
  },
  methods: {
    wrapText (text, width) {
      text.each(function () {
        let text = select(this)
        let words = text.text().split(/\s+/).reverse()
        let word
        let line = []
        let lineNumber = 0
        let lineHeight = 1.1
        let y = text.attr('y')
        let dy = parseFloat(text.attr('dy'))
        let tspan = text.text(null)
          .append('tspan')
          .attr('x', 0)
          .attr('y', y)
          .attr('dy', dy + 'em')

        while (words.length) {
          word = words.pop()
          line.push(word)
          tspan.text(line.join(' '))
          if (tspan.node().getComputedTextLength() > width) {
            line.pop()
            tspan.text(line.join(' '))
            line = [word]
            tspan = text.append('tspan')
            .attr('x', 0)
            .attr('y', y)
            .attr('dy', ++lineNumber * lineHeight + dy + 'em')
            .text(word)
          }
        }
      })
    },

    // Line chart ================
    drawLine (i) {
      let height = this.height
      // Offset moves subsequent multi line down
      let offset = (this.offset === 2) ? i * Math.round(this.height / 5) : 0
      // let shift = (this.offset === 2) ? offset : 0
      // offset = (this.offset === 1) ? i * 48 : 0

      let start = (this.offset === 1) ? i * 60 : 0
      let rangeStart = this.baseStart

      // Add padding to right for background chart
      let spaceRight = (typeof this.back === 'number') ? this.back : 0
      let x = scalePoint()
        .range([0, this.width - spaceRight])

      let y = scaleLinear()
        .range([height, start])

      // Draw line
      let valueLine = line()
        .x((d) => { return x(d.key) })
        .y((d) => { return y(d.number) })

      // Area
      let valueArea = area()
        .x((d) => { return x(d.key) })
        .y0(height - offset)
        .y1((d) => { return y(d.number) })

      let data = this.chartData[i].values.map((d, i) => {
        return {
          key: d.key,
          value: d.value,
          number: Number(d.value.replace(/[^0-9.-]+/g, ''))
        }
      })

      if (this.skew > 0) {
        rangeStart = max(data, (d) => { return d.number }) / (100 / this.skew)
      }

      x.domain(data.map((d) => { return d.key }))
      y.domain([rangeStart, max(data, (d) => { return d.number })])

      let areaColor = (Array.isArray(this.color)) ? this.color[i] : this.color

      let group = this.canvas.append('g')
        .attr('class', 'o-chart__group')
        .attr('transform', 'translate(0, ' + offset + ')')

      // Append the area
      group.append('path')
        .attr('class', 'o-chart__area o-chart__area--' + areaColor + '--v')
        .attr('d', valueArea(data))

      // Append the line
      group.append('path')
        .attr('class', 'o-chart__line')
        .attr('d', valueLine(data))

      // Append the scatterplot
      group.append('g')
        .selectAll('dot')
        .data(data)
        .enter().append('circle')
        .attr('class', 'o-chart__point')
        .attr('r', 4)
        .attr('cx', (d) => { return x(d.key) })
        .attr('cy', (d) => { return y(d.number) })

      let labelOffset = (i === 1) ? 20 : -10
      // Add labels
      let labelGroup = group.append('g')
        .attr('class', 'o-chart__labels')
        .attr('transform', 'translate(0, ' + labelOffset + ')')
      let labelItem = labelGroup.selectAll('g')
        .data(data)
        .enter().append('g')
      // labelItem.append('rect')
      //   .attr('class', 'o-chart__back')
      labelItem.append('text')
        .attr('text-anchor', 'middle')
        .attr('class', 'o-chart__label')
        .attr('x', function (d) {
          // QUick fix to move label from area
          let shift = (d.value === '0.05') ? 7 : 0
          return Math.round(x(d.key) + shift)
        })
        .attr('y', function (d) { return Math.round(y(d.number)) })
        .text((d, i) => {
          // hack to show NA figure in customer chart
          return (this.replaceNa && i === 0) ? 'NA' : d.value
        })
        .each(function (d, i) {
          d.bb = this.getBBox()
        })

      // DrawCircles
      if (this.back && this.offset === 2) {
        let index = data.length - 1
        let circleOffset = (i === 1) ? 8 : -8
        group.append('circle')
          .attr('class', 'o-chart__circle')
          .attr('r', 20)
          .attr('cx', Math.round(x(data[index].key)))
          .attr('cy', Math.round(y(data[index].number) + circleOffset))
      }

      // Draw double circle
      if (this.back && this.offset === 1 && i === this.chartData.length - 1) {
        let index = data.length - 1
        group.append('circle')
          .attr('class', 'o-chart__circle')
          .attr('r', 26)
          .attr('cx', Math.round(x(data[index].key)))
          .attr('cy', Math.round(y(data[index].number)))
      }

      // Only render 1 axis
      if (i !== this.chartData.length - 1) return

      // console.log(data)
      // let axisMargin = {
      //   left: Math.round(data[0].bb.width / 2),
      //   right: Math.round(data[data.length - 1].bb.width / 2)
      // }

      // Slightly narrower axis
      // let xNarrow = scalePoint()
      //   .domain(data.map((d) => { return d.key }))
      //   .range([axisMargin.left, this.width - axisMargin.right - spaceRight])

      // Append the X axis
      this.canvas.append('g')
        .attr('class', 'o-chart__x-axis')
        .attr('transform', 'translate(0,' + (height - 4) + ')')
        .call(axisBottom(x))

      // labelItem.selectAll('rect')
      //   .attr('x', function (d) { return Math.round(x(d.key) - (d.bb.width / 2)) })
      //   .attr('y', function (d) { return Math.round(y(d.number) - d.bb.height + 3) })
      //   .attr('width', function (d) { return d.bb.width })
      //   .attr('height', function (d) { return d.bb.height - 2 })
    },

    // Bar chart ================
    drawBar (i) {
      let rangeStart = 0
      let _this = this
      let x = scaleBand()
        .range([0, this.width])
        .paddingInner(this.padding)

      let y = scaleLinear()
        .range([this.height, 0])

      let data = this.chartData[i].values.map((d, i) => {
        return {
          key: d.key,
          value: d.value,
          number: Number(d.value.replace(/[^0-9.-]+/g, ''))
        }
      })

      if (this.skew > 0) {
        rangeStart = max(data, (d) => { return d.number }) / (100 / this.skew)
      }

      x.domain(data.map((d) => { return d.key }))
      y.domain([rangeStart, max(data, (d) => { return d.number })])

      // Append teh bars
      this.canvas.selectAll('rect')
        .data(data)
        .enter().append('rect')
        .attr('class', 'o-chart__bar')
        .attr('x', function (d) { return Math.round(x(d.key)) })
        .attr('y', function (d) { return Math.round(y(d.number)) })
        .attr('width', Math.round(x.bandwidth()))
        .attr('height', function (d) { return Math.round(_this.height - y(d.number)) })

      // Append the X axis
      this.canvas.append('g')
        .attr('class', 'o-chart__x-axis')
        .attr('transform', 'translate(0,' + (this.height - 4) + ')')
        .call(axisBottom(x))
        .selectAll('.tick text')
        .call(this.wrapText, x.bandwidth())

      // Add labels
      let labelGroup = this.canvas.append('g')
        .attr('class', 'o-chart__labels')
        .attr('transform', 'translate(0, -8)')
      let labelItem = labelGroup.selectAll('g')
        .data(data)
        .enter().append('g')
      labelItem.append('text')
        .attr('text-anchor', 'middle')
        .attr('class', 'o-chart__label')
        .attr('x', function (d) { return Math.round(x(d.key)) + (x.bandwidth() / 2) })
        .attr('y', function (d) { return Math.round(y(d.number)) })
        .text(function (d) { return d.value })
    },
    drawBackground () {
      let bg = this.canvas.append('g')
        .attr('class', 'o-chart__bg')
      bg.append('rect')
        .attr('class', 'o-chart__bg__fill')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', this.width)
        .attr('height', this.height)
      bg.append('line')
        .attr('class', 'o-chart__bg__stroke')
        .attr('stroke-dasharray', '6, 2')
        .attr('x1', 0)
        .attr('y1', 0.5)
        .attr('x2', this.width)
        .attr('y2', 0.5)
      bg.append('line')
        .attr('class', 'o-chart__bg__stroke')
        .attr('stroke-dasharray', '6, 2')
        .attr('x1', 0)
        .attr('y1', this.height - 0.5)
        .attr('x2', this.width)
        .attr('y2', this.height - 0.5)

      let sideline = bg.append('g')
        .attr('transform', 'translate(8, 0)')
      sideline.append('line')
        .attr('class', 'o-chart__bg__stroke')
        .attr('x1', this.width - 0.5)
        .attr('y1', 0)
        .attr('x2', this.width - 0.5)
        .attr('y2', this.height)
      sideline.append('line')
        .attr('class', 'o-chart__bg__stroke')
        .attr('x1', this.width - 4)
        .attr('y1', 0.5)
        .attr('x2', this.width + 3)
        .attr('y2', 0.5)
      sideline.append('line')
        .attr('class', 'o-chart__bg__stroke')
        .attr('x1', this.width - 4)
        .attr('y1', this.height - 0.5)
        .attr('x2', this.width + 3)
        .attr('y2', this.height - 0.5)
    },
    redraw () {
      this.clientWidth = this.$el.clientWidth
      let elem = this.$el.querySelector('.o-chart__canvas')
      elem.parentNode.removeChild(elem)
      this.renderChart()
    },
    renderChart () {
      this.svg
        .attr('width', this.width + this.margin.left + this.margin.right)
        .attr('height', this.height + this.margin.top + this.margin.bottom)
      this.canvas = this.svg.append('g')
        .attr('class', 'o-chart__canvas')
        .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')')

      this.chartData = this.origData.columns.slice(1).map((id) => {
        return {
          id: id,
          values: this.origData.map((d) => {
            return {
              key: d.key,
              value: d[id]
            }
          })
        }
      })

      // Draw background
      if (this.back) {
        this.drawBackground()
      }

      // console.log(this.chartData)

      switch (this.chartType) {
        case 'line':
          this.chartData.forEach((d, i) => {
            this.drawLine(i)
          })
          break
        case 'bar':
          this.drawBar(0)
          break
      }
    }
  },
  mounted () {
    this.clientWidth = this.$el.clientWidth
    if (this.clientWidth > 0) {
      this.svg = select(this.$refs.svg)
      this.renderChart()
      window.addEventListener('resize', debounce(this.redraw, 1000))
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/standard/my-chart.js